import React from 'react';
import type { HardwareGuides } from '../../assets/hardwareGuides';

const ANCHOR_STROKE = '#ff2d55';
const ZONE_STROKE = '#00c7be';
const MARKER_STROKE_WIDTH = 2;
const ANCHOR_RADIUS = 10;
const CROSSHAIR = 12;

export function shouldShowHardwareGuideDebug(override?: boolean): boolean {
  return override === true;
}

/**
 * Development-only overlay. Drawn in authored slab space from parsed
 * `#hardware-guides` geometry. Not slab artwork and not recoloured.
 */
export const HardwareGuidesDebugOverlay: React.FC<{
  guides: HardwareGuides;
  /** Anchor ids that already have product artwork. Debug markers stay for the rest. */
  occupiedAnchorIds?: string[];
}> = ({ guides, occupiedAnchorIds = [] }) => {
  const occupied = new Set(occupiedAnchorIds);
  const anchors = guides.anchors.filter((anchor) => !occupied.has(anchor.id));
  if (anchors.length === 0 && guides.zones.length === 0) return null;

  return (
    <g data-part="door-hardware-guides-debug" pointerEvents="none" aria-hidden="true">
      {guides.zones.map((zone) => (
        <rect
          key={zone.id || `${zone.x},${zone.y}`}
          data-part="door-hardware-debug-zone"
          data-hardware-guide-id={zone.id}
          data-hardware-role={zone.role}
          data-zone-type={zone.zoneType}
          data-position={zone.position}
          x={zone.x}
          y={zone.y}
          width={zone.width}
          height={zone.height}
          fill="none"
          stroke={ZONE_STROKE}
          strokeWidth={MARKER_STROKE_WIDTH}
        />
      ))}
      {anchors.map((anchor) => (
        <g
          key={anchor.id || `${anchor.x},${anchor.y}`}
          data-part="door-hardware-debug-anchor"
          data-hardware-guide-id={anchor.id}
          data-hardware-role={anchor.role}
          data-anchor-type={anchor.anchorType}
          data-position={anchor.position}
        >
          <circle
            cx={anchor.x}
            cy={anchor.y}
            r={ANCHOR_RADIUS}
            fill="none"
            stroke={ANCHOR_STROKE}
            strokeWidth={MARKER_STROKE_WIDTH}
          />
          <line
            x1={anchor.x - CROSSHAIR}
            y1={anchor.y}
            x2={anchor.x + CROSSHAIR}
            y2={anchor.y}
            stroke={ANCHOR_STROKE}
            strokeWidth={MARKER_STROKE_WIDTH}
          />
          <line
            x1={anchor.x}
            y1={anchor.y - CROSSHAIR}
            x2={anchor.x}
            y2={anchor.y + CROSSHAIR}
            stroke={ANCHOR_STROKE}
            strokeWidth={MARKER_STROKE_WIDTH}
          />
        </g>
      ))}
    </g>
  );
};
